import * as React from 'react'

import { withDefaultErrorBoundary } from '@/modules/shared/hoc/withDefaultErrorBoundary'
import { useQueryConfigurationBusinessTrips } from '@/lib/api/company/configuration/businessTrips/useQueryConfigurationBusinessTrips'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import {
  Checkbox,
  FieldLabel,
  FieldLabelRight,
  Input,
} from '@/ui/form/primitives'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { FieldsetRadio } from '../../components/FieldsetRadio'
import { FieldsetInput } from '../../components/FieldsetInput'
import { TimeField } from '@/ui/form/controls/TimeField'
import { CompanyCountriesSection } from './sections/CompanyCountriesSelector'

import {
  CALCULATION_DAY_TYPES,
  COUNTRY_SOURCES,
  DAY_TYPES,
  EUROPE_MODES,
  GENERATE_TRIP_ACCOMODATION_OPTIONS,
  GENERATED_TRIP_COUNTRY_SOURCE_TYPES,
  GENERATION_MODES,
  HOMELAND_ALLOWANCE_DURATION_SOURCES,
  SANITARITY_COST_MODES,
  SANITARITY_MODES,
  shouldDisplayTripCurrencyTableDaysTypeAdditionalField,
  shouldDisplayTripMonthTypeField,
  TRIP_MONTH_TYPES,
} from './config'
import { getGenerateTripAccommodationsName } from '@/app/common/converters/configuration/generateTripAccommodationsTextConverter'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { GenerateTripAccommodations } from '@/app/constants/configuration/generateTripAccommodations'
import { getGeneratedTripCountrySourceTypeName } from '@/app/common/converters/configuration/generatedTripCountrySourceTypeToTextConverter'
import { GeneratedTripCountrySourceType } from '@/app/constants/configuration/generatedTripCountrySourceType'
import { formatDate } from '@/modules/shared/utils/date'
import { TripPartEuropeMode } from '@/app/constants/configuration/tripPartEuropeMode'
import { getTripPartEuropeModeName } from '@/app/common/converters/configuration/tripPartEuropeModeToTextConverter'
import { TripSanitarityMode } from '@/app/constants/configuration/tripSanitarityMode'
import { getTripSanitarityModeName } from '@/app/common/converters/configuration/tripSanitarityModeToTextConverter'
import { TripSanitarityCostMode } from '@/app/constants/configuration/tripSanitarityCostMode'
import { getTripSanitarityCostModeName } from '@/app/common/converters/configuration/tripSanitarityCostModeToTextConverter'
import { TripHomelandAllowanceDurationSource } from '@/app/constants/configuration/tripHomelandAllowanceDurationSource'
import { getTripHomelandAllowanceDurationSourceName } from '@/app/common/converters/configuration/tripHomelandAllowanceDurationSourceToTextConverter'
import { DaysType } from '@/app/constants/configuration/daysType'
import { getDaysTypeName } from '@/app/common/converters/configuration/daysTypeToTextConverter'
import { TripMonthType } from '@/app/constants/configuration/tripMonthType'
import { getTripMonthTypeName } from '@/app/common/converters/configuration/tripMonthTypeToTextConverter'
import { TripPartGenerationMode } from '@/app/constants/configuration/tripPartGenerationMode'
import { getTripPartGenerationModeName } from '@/app/common/converters/configuration/tripPartGenerationModeToTextConverter'
import { CountrySource } from '@/app/constants/configuration/countrySource'
import { getCountrySourceName } from '@/app/common/converters/configuration/countrySourceToTextConverter'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'
import { CountryModel } from '@/modules/shared/types/country'
import { RelativeDaysOrMonthsFields } from './RelativeDateSelector'
import { getDaysType } from '@/app/services/configuration/daysTypeResolver'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { useConfigurationBusinessTripMutation } from '@/lib/api/company/configuration/businessTrips/useMutationConfigurationBusinessTrip'
import { BusinessTripConfigurationModel } from '@/lib/api/company/configuration/businessTrips/types'
import { toPriorityCountries } from '@/lib/api/company/configuration/businessTrips/priorityCountry'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { QUERY_KEY_SETTINGS_BUSINESS_TRIP } from '@/lib/api/constants'

const YES_VALUE = '1'
const NO_VALUE = '0'

export const PRIORITY_COUNTRIES_COUNT = 8

const getBooleanRadioOptions = (t: (key: string) => string) => [
  { value: YES_VALUE, label: t('common.yes') },
  { value: NO_VALUE, label: t('common.no') },
]

const dateInputHelper = {
  toInputDate: (ms?: number | null) => {
    if (!ms && ms !== 0) return '' // EMPTY to show placeholder
    const d = new Date(ms)
    const tzOffset = d.getTimezoneOffset() * 60 * 1000
    const local = new Date(d.getTime() - tzOffset)
    return local.toISOString().slice(0, 10)
  },
  fromInputDate: (value: string): number | null => {
    if (!value) return null // user cleared the field
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d).getTime()
  },
}

// Business trip configuration page
const BusinessTripPageContent = (
  props: RouteComponentProps<{ companyId: string }>,
) => {
  const companyId = props.match.params.companyId
  const q = useQueryConfigurationBusinessTrips(companyId)
  const { t } = useLocalize()
  const [state, setState] =
    React.useState<BusinessTripConfigurationModel | null>(null)
  const lastQueryData = React.useRef<BusinessTripConfigurationModel | null>(
    null,
  )

  const mutation = useConfigurationBusinessTripMutation(companyId)

  React.useEffect(() => {
    if (!q.isFetching && q.data) {
      const newState = q.data //convertApiDataToState(q.data)
      const prevServerData = lastQueryData.current
      lastQueryData.current = newState
      setState((prev) => {
        // Initial load: always set
        if (!prev) return newState
        // No local changes since last fetch: accept server update
        if (JSON.stringify(prev) === JSON.stringify(prevServerData))
          return newState
        // User has unsaved changes: keep them
        return prev
      })
    }
  }, [q.data, q.isFetching])

  const setValue = (name, value) => {
    setState((state) => ({
      ...state,
      [name]: value,
    }))
  }

  const handleChangeBoolean = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, checked } = e.target
      setState((state) => ({
        ...state,
        [name]: checked,
      }))
    },
    [],
  )

  const readOnly = false

  const isDirty = React.useMemo(() => {
    return JSON.stringify(state) !== JSON.stringify(q.data)
  }, [state, q.data])

  // Serwer trzyma państwa szybkiego wyboru w 8 kolumnach i odrzuca krótszą listę.
  const hasInvalidPriorityCountries =
    !!state &&
    (state.priorityCountries?.length ?? 0) !== PRIORITY_COUNTRIES_COUNT

  const fieldLabels = React.useMemo<Record<string, string>>(() => ({
    tripAccomodationCountrySource: t('configuration-businessTrip.accomodation-country-source'),
    tripSetMultipleDestinationCountries: t('configuration-businessTrip.multiple-destination-countries'),
    tripAddAccomodationOnlyForValidPolishNightRest: t('configuration-businessTrip.polish-night-rest-check'),
    addAccommodationForFerryTrain: t('configuration-businessTrip.ferry-accommodation'),
    generateTripAccommodations: t('configuration-businessTrip.auto-accommodations'),
    skipRegularRestForAccommodations: t('configuration.businessTrips.skipRegularRestForAccommodations'),
    minimalRestForAccommodation: t('configuration.businessTrips.minimalRestForAccommodation'),
    tripPartType: t('configuration-businessTrip.domestic-sections-analysis'),
    tripPartForeignCountryMode: t('configuration-businessTrip.foreign-sections-analysis'),
    useExchangeRatesTableB: t('configuration-businessTrip.use-nbp-exchange-rates'),
    isBeginEndGpsOptionEnabled: t('configuration-businessTrip.gps-begin-end-option'),
    showAmountsInCurrenciesSummaryOnDetailedTripDocument: t('configuration-businessTrip.show-currency-summary'),
    showAmountToConfirmInEurForDetaildTripPrint: t('configuration-businessTrip.show-eur-confirm-amount'),
    showConfirmedAmountsOnBusinessTrip: t('configuration-businessTrip.show-confirmed-amounts'),
    tripCurrencyTableDaysType: t('configuration-businessTrip.currency-rate-date'),
    tripCurrencyTableDaysCount: `${t('configuration-businessTrip.currency-rate-date')} (dni)`,
    tripCurrencyTableMonth: `${t('configuration-businessTrip.currency-rate-date')} (miesiąc)`,
    tripCalculationDateDaysType: t('configuration-businessTrip.settlement-date'),
    tripCalculationDateDaysCount: `${t('configuration-businessTrip.settlement-date')} (dni)`,
    tripCalculationDateMonth: `${t('configuration-businessTrip.settlement-date')} (miesiąc)`,
    homelandAllowanceDurationSource: t('configuration-businessTrip.homeland-allowance-source'),
    tripNumberPattern: t('configuration-businessTrip.trip-number-pattern'),
    tripPartEuropeModeStartFrom: t('configuration-businessTrip.virtual-allowance-60eur-from'),
    tripPartEuropeMode: t('configuration-businessTrip.virtual-allowance-60eur-mode'),
    tripAccommodationInvoiceGeneration: t('configuration-businessTrip.accommodation-invoice'),
    tripSanitarityMode: t('configuration-businessTrip.sanitary-mode'),
    skipSanitarityOnAccommodationDays: t('configuration.businessTrips.skipSanitarityOnAccommodationDays'),
    tripSanitarityCostMode: t('configuration-businessTrip.sanitary-cost-mode'),
    priorityCountries: t('configuration-businessTrip.quick-select-countries'),
    generatedTripCountrySource: t('configuration-businessTrip.border-crossing-source'),
    extendCountryChangesSearch: t('configuration-businessTrip.extended-border-search'),
    includeTmsLoads: t('configuration-businessTrip.include-tms-loads'),
    isGeneratedTripTransit: t('configuration-businessTrip.borders-excluded-from-delegation'),
    startTripsFromDriving: t('configuration-businessTrip.start-trips-from-driving'),
    minDrivingMinutesForStartOnDriving: t('configuration-businessTrip.min-driving-minutes-start-on-driving'),
    endTripsOnDriving: t('configuration-businessTrip.end-trips-on-driving'),
    minDrivingMinutesForEndOnDriving: t('configuration-businessTrip.min-driving-minutes-end-on-driving'),
    skipManualEntrisOnAutoTrips: t('configuration-businessTrip.skip-manual-entries'),
    maxManualEntryOnAutoTrips: `${t('configuration-businessTrip.skip-manual-entries')} (max)`,
    skipNoDataOnAutoTrips: t('configuration-businessTrip.skip-no-data'),
    maxNoDataOnAutoTrips: `${t('configuration-businessTrip.skip-no-data')} (max)`,
  }), [t])

  const fieldFormatters = React.useMemo<Record<string, (value: unknown) => string>>(() => ({
    generateTripAccommodations: (v) => getGenerateTripAccommodationsName(v as GenerateTripAccommodations, t),
    tripPartType: (v) => getTripPartGenerationModeName(v as TripPartGenerationMode, t, true),
    tripPartForeignCountryMode: (v) => getTripPartGenerationModeName(v as TripPartGenerationMode, t),
    tripAccomodationCountrySource: (v) => getCountrySourceName(v as CountrySource, t),
    tripPartEuropeMode: (v) => getTripPartEuropeModeName(v as TripPartEuropeMode, t),
    tripSanitarityMode: (v) => getTripSanitarityModeName(v as TripSanitarityMode, t),
    tripSanitarityCostMode: (v) => getTripSanitarityCostModeName(v as TripSanitarityCostMode, t),
    homelandAllowanceDurationSource: (v) => getTripHomelandAllowanceDurationSourceName(v as TripHomelandAllowanceDurationSource, t),
    generatedTripCountrySource: (v) => getGeneratedTripCountrySourceTypeName(v as GeneratedTripCountrySourceType, t),
    tripCurrencyTableDaysType: (v) => getDaysTypeName(getDaysType(v as DaysType), t),
    tripCalculationDateDaysType: (v) => getDaysTypeName(getDaysType(v as DaysType), t),
    tripCurrencyTableMonth: (v) => getTripMonthTypeName(v as TripMonthType, t),
    tripCalculationDateMonth: (v) => getTripMonthTypeName(v as TripMonthType, t),
  }), [t])

  const fieldRenderers = React.useMemo<
    Record<
      string,
      (
        before: unknown,
        after: unknown,
      ) => { before: React.ReactNode; after: React.ReactNode }
    >
  >(() => ({
    priorityCountries: (before, after) => {
      const beforeArr = (before as CountryModel[]) ?? []
      const afterArr = (after as CountryModel[]) ?? []
      const removed = beforeArr.filter((b) => !afterArr.some((a) => a.id === b.id))
      const added = afterArr.filter((a) => !beforeArr.some((b) => b.id === a.id))
      return {
        before: removed.length > 0
          ? <span className="tw-line-through">{removed.map((c) => c.name).join(', ')}</span>
          : <span className="tw-text-gray-400">—</span>,
        after: added.length > 0
          ? <>{added.map((c) => c.name).join(', ')}</>
          : <span className="tw-text-gray-400">—</span>,
      }
    },
  }), [])

  const handleReset = () => {
    setState(lastQueryData.current)
  }

  const handleSave = () => {
    mutation.mutate(state)
  }

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={q.isFetching || mutation.isPending}
      isError={!!q.isError}
      errorMsg={q.isError ? t('configuration-businessTrip.load-error') : ''}
      isDirty={isDirty}
      hasValidationError={hasInvalidPriorityCountries}
      handleSave={handleSave}
      handleReset={handleReset}
      restore={{
        configurationType: ConfigurationType.BusinessTrip,
        tabLabelKey: 'configuration-menu.business-trips',
        queryKeys: [QUERY_KEY_SETTINGS_BUSINESS_TRIP],
        serverData: q.data,
        withDefaults: true,
      }}
      globalErrorMsg={t('configuration-businessTrip.load-error-global')}
      diffBefore={q.data as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={fieldLabels}
      diffFormatters={fieldFormatters}
      diffRenderers={fieldRenderers}
    >
      {state && (
        <SettingsSectionWithTabs title="">
          <div
            data-tab-name="a"
            title={t('configuration-businessTrip.tab-basic')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* 
          PL_old: Źródło informacji o zmianie kraju dla noclegów
          PL: Informacja o zmianie krajów do noclegów
          Api: tripAccomodationCountrySource
          */}
            <FieldsetSelect
              label={t(
                'configuration-businessTrip.accomodation-country-source',
              )}
              options={COUNTRY_SOURCES.map((key) => ({
                key,
                value: key.toString(),
                label: getCountrySourceName(key, t),
              }))}
              value={state.tripAccomodationCountrySource.toString()}
              handleChange={(_, { option }) =>
                setValue(
                  'tripAccomodationCountrySource',
                  option.key as CountrySource,
                )
              }
              name="tripAccomodationCountrySource"
              disabled={readOnly}
            />

            {/* 
          PL_old: Ustawiaj wiele państw docelowych przy wielokrotnej zmianie krajów
          PL: Rozliczaj delegacje wg wielu państw docelowych
          API: tripSetMultipleDestinationCountries
          */}
            <FieldsetSwitch
              label={t(
                'configuration-businessTrip.multiple-destination-countries',
              )}
              checked={state.tripSetMultipleDestinationCountries}
              onChange={handleChangeBoolean}
              name="tripSetMultipleDestinationCountries"
              disabled={readOnly}
            />

            {/* 
          PL_old: Sprawdzaj dla noclegów w Polsce odpoczynek 6h w godzinach 21:00–7:00 
          PL: Naliczaj nocleg wg rozp. ministra dot. podróży służbowych w Polsce
          API: tripAddAccomodationOnlyForValidPolishNightRest
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.polish-night-rest-check')}
              checked={state.tripAddAccomodationOnlyForValidPolishNightRest}
              onChange={handleChangeBoolean}
              name="tripAddAccomodationOnlyForValidPolishNightRest"
              disabled={readOnly}
            />

            {/* 
          PL_old: Dodawaj nocleg na odpoczynkach promowych 
          PL: Wstawiaj nocleg w trakcie odpoczynków promowych
          API: addAccommodationForFerryTrain
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.ferry-accommodation')}
              checked={state.addAccommodationForFerryTrain}
              onChange={handleChangeBoolean}
              name="addAccommodationForFerryTrain"
              disabled={readOnly}
            />

            {/* 
          ST: Automatycznie generuj noclegi
          PL_old: Automatycznie generuj noclegi 
          PL: Automatycznie wstawiaj noclegi
          API: generateTripAccommodations 
          API optional checkbox: skipRegularRestForAccommodations
          API: optional time: generateTripAccommodationsTime @missing
          */}
            <FieldsetSelect
              label={t('configuration-businessTrip.auto-accommodations')}
              options={GENERATE_TRIP_ACCOMODATION_OPTIONS.map((value) => ({
                key: value,
                value: value.toString(),
                label: getGenerateTripAccommodationsName(value, t),
              }))}
              value={state.generateTripAccommodations.toString()}
              // onChange={() => { }}
              handleChange={(_, { option }) =>
                setValue(
                  'generateTripAccommodations',
                  option.key as GenerateTripAccommodations,
                )
              }
              name="generateTripAccommodations"
              disabled={readOnly}
            >
              <div>
                {(state.generateTripAccommodations.toString() ==
                  GenerateTripAccommodations.True.toString() ||
                  state.generateTripAccommodations.toString() ==
                    GenerateTripAccommodations.OnlyForValidRests.toString()) && (
                  <div className="tw-my-2 tw-flex tw-items-center tw-gap-1">
                    <Checkbox
                      checked={state.skipRegularRestForAccommodations}
                      onChange={(e) =>
                        setValue(
                          'skipRegularRestForAccommodations',
                          e.target.checked,
                        )
                      }
                    />
                    <label className="tw-mb-0">
                      {t(
                        'configuration.businessTrips.skipRegularRestForAccommodations',
                      )}
                    </label>
                  </div>
                )}
                {state.generateTripAccommodations.toString() ==
                  GenerateTripAccommodations.OnlyForValidRests.toString() && (
                  <div className="tw-my-2 tw-flex tw-items-center tw-gap-2">
                    <TimeField
                      label={t(
                        'configuration.businessTrips.minimalRestForAccommodation',
                      )}
                      value={(state.minimalRestForAccommodation || '').slice(
                        0,
                        5,
                      )}
                      setValue={(v) =>
                        setValue(
                          'minimalRestForAccommodation',
                          v ? `${v}:00` : '',
                        )
                      }
                      disabled={readOnly}
                    />
                  </div>
                )}
              </div>
            </FieldsetSelect>

            {/* 
          PL_old: Grupowanie odcinków krajowych 
          PL: Analizowanie odcinków krajowych
          API: tripPartType
          */}
            <FieldsetSelect
              label={t('configuration-businessTrip.domestic-sections-analysis')}
              options={GENERATION_MODES.map((key) => ({
                key,
                value: key.toString(),
                label: getTripPartGenerationModeName(key, t, true),
              }))}
              value={state.tripPartType.toString()}
              handleChange={(_, { option }) =>
                setValue('tripPartType', option.key as TripPartGenerationMode)
              }
              name="tripPartType"
              disabled={readOnly}
            />

            {/* 
          PL_old: Grupowanie odcinków zagranicznych 
          PL: Analizowanie odcinków zagranicznych
          API: tripPartForeignCountryMode
          */}
            <FieldsetSelect
              label={t('configuration-businessTrip.foreign-sections-analysis')}
              options={GENERATION_MODES.map((key) => ({
                key,
                value: key.toString(),
                label: getTripPartGenerationModeName(key, t),
              }))}
              value={state.tripPartForeignCountryMode.toString()}
              handleChange={(_, { option }) =>
                setValue(
                  'tripPartForeignCountryMode',
                  option.key as TripPartGenerationMode,
                )
              }
              name="tripPartForeignCountryMode"
              disabled={readOnly}
            />

            {/* 
          PL_old: Wykorzystaj w delegacji kursy walut z tabeli NBP
          PL: Przyjmuj w delegacji kursy walut z tabeli NBP
          API: useExchangeRatesTableB
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.use-nbp-exchange-rates')}
              checked={state.useExchangeRatesTableB}
              onChange={handleChangeBoolean}
              name="useExchangeRatesTableB"
              disabled={readOnly}
            />

            {/* 
          PL_old: Umożliwiaj wskazanie początku i końca delegacji podczas importu z GPS
          PL: Przyjmuj początek i koniec delegacji podczas importu z GPS
          API: isBeginEndGpsOptionEnabled
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.gps-begin-end-option')}
              checked={state.isBeginEndGpsOptionEnabled}
              onChange={handleChangeBoolean}
              name="isBeginEndGpsOptionEnabled"
              disabled={readOnly}
            />

            {/* 
          PL_old: Umieszczaj podsumowanie w walutach na szczegółowym rozliczeniu podróży służbowej
          PL: Wyświetlaj podsumowanie w walutach na szczegółowym rozliczeniu podróży służbowej
          API: showAmountsInCurrenciesSummaryOnDetailedTripDocument
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.show-currency-summary')}
              checked={
                state.showAmountsInCurrenciesSummaryOnDetailedTripDocument
              }
              onChange={handleChangeBoolean}
              name="showAmountsInCurrenciesSummaryOnDetailedTripDocument"
              disabled={readOnly}
            />

            {/* 
          PL_old: Umieszczaj na rozliczeniu szczegółowym podróży służbowej kwotę do zatwierdzenia w euro
          PL: Wyświetlaj na rozliczeniu szczegółowym podróży służbowej kwotę do zatwierdzenia w euro
          API: showAmountToConfirmInEurForDetaildTripPrint
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.show-eur-confirm-amount')}
              checked={state.showAmountToConfirmInEurForDetaildTripPrint}
              onChange={handleChangeBoolean}
              name="showAmountToConfirmInEurForDetaildTripPrint"
              disabled={readOnly}
            />

            {/* 
          PL_old: Umieszczaj zatwierdzenie kwoty na poleceniu wyjazdu służbowego 
          PL: Wyświetlaj zatwierdzenie kwoty na poleceniu wyjazdu służbowego
          API: showConfirmedAmountsOnBusinessTrip
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.show-confirmed-amounts')}
              checked={state.showConfirmedAmountsOnBusinessTrip}
              onChange={handleChangeBoolean}
              name="showConfirmedAmountsOnBusinessTrip"
              disabled={readOnly}
            />

            {/* 
          PL_old: Tabela walut z dnia 
          PL: Kurs waluty z dnia
          API: tripCurrencyTableDaysType
          API number: tripCurrencyTableDaysCount
          API month: tripCurrencyTableMonth
          */}
            <RelativeDaysOrMonthsFields
              label={t('configuration-businessTrip.currency-rate-date')}
              options={DAY_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getDaysTypeName(getDaysType(value), t),
              }))}
              selected={state.tripCurrencyTableDaysType?.toString() || ''}
              handleSelect={(option) =>
                setValue('tripCurrencyTableDaysType', option.key as DaysType)
              }
              relativeMonthSelected={state.tripCurrencyTableDaysCount}
              relativeMonthHandleSelect={(num) =>
                setValue('tripCurrencyTableDaysCount', num)
              }
              relativeDaysSelected={state.tripCurrencyTableDaysCount}
              relativeDaysHandleSelect={(num) =>
                setValue('tripCurrencyTableDaysCount', num)
              }
              monthTypeHandleSelect={(option) =>
                setValue('tripCurrencyTableMonth', option.key)
              }
              monthTypeSelected={state.tripCurrencyTableMonth}
              monthTypeOptions={TRIP_MONTH_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTripMonthTypeName(value, t),
              }))}
            />

            {/* 
          PL_old: Data rozliczenia 
          PL: Data rozliczenia delegacji
          API: tripCalculationDateDaysType
          API select: tripCalculationDateDaysType
          API number: tripCalculationDateDaysCount
          API month: tripCalculationDateMonth
          */}
            <RelativeDaysOrMonthsFields
              label={t('configuration-businessTrip.settlement-date')}
              options={CALCULATION_DAY_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getDaysTypeName(getDaysType(value), t),
              }))}
              selected={state.tripCalculationDateDaysType?.toString() || ''}
              handleSelect={(option) =>
                setValue('tripCalculationDateDaysType', option.key as DaysType)
              }
              relativeMonthSelected={state.tripCalculationDateDaysCount}
              relativeMonthHandleSelect={(num) =>
                setValue('tripCalculationDateDaysCount', num)
              }
              relativeDaysSelected={state.tripCalculationDateDaysCount}
              relativeDaysHandleSelect={(num) =>
                setValue('tripCalculationDateDaysCount', num)
              }
              monthTypeHandleSelect={(option) =>
                setValue('tripCalculationDateMonth', option.key)
              }
              monthTypeSelected={state.tripCalculationDateMonth?.toString()}
              monthTypeOptions={TRIP_MONTH_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTripMonthTypeName(value, t),
              }))}
            />

            {/* 
          PL_old: Rozliczanie diet krajowych podczas podróży zagranicznych według 
          PL: Naliczaj diety krajowe według
          API: homelandAllowanceDurationSource
          */}
            <FieldsetRadio
              label={t('configuration-businessTrip.homeland-allowance-source')}
              options={HOMELAND_ALLOWANCE_DURATION_SOURCES.map((key) => ({
                key,
                value: key.toString(),
                label: getTripHomelandAllowanceDurationSourceName(key, t),
              }))}
              value={state.homelandAllowanceDurationSource.toString()}
              handleChange={(_, { option }) =>
                setValue(
                  'homelandAllowanceDurationSource',
                  option.key as TripHomelandAllowanceDurationSource,
                )
              }
              name="homelandAllowanceDurationSource"
              disabled={readOnly}
            />

            {/* 
          PL_old: Schemat numerowania delegacji 
          PL: Schemat numerowania podróży służbowej
          API: tripNumberPattern
          */}
            <FieldsetInput
              label={t('configuration-businessTrip.trip-number-pattern')}
              value={state.tripNumberPattern}
              name="tripNumberPattern"
              placeholder=""
              onChange={(e) => setValue('tripNumberPattern', e.target.value)}
              disabled={readOnly}
              type="text"
              // @TODO description
              bottomInfo={t(
                'configuration-businessTrip.trip-number-pattern-help',
              )}
            />
          </div>

          {/* 
        "Ustawa z dnia 19 sierpnia 2023
        */}
          <div
            data-tab-name="b"
            title={t('configuration-businessTrip.tab-act-2023')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* 
          PL_old: Naliczaj diety 60 EUR od 
          PL: Naliczaj “wirtualne diety” w wysokości 60 EUR od
          API: tripPartEuropeModeStartFrom
          */}
            <FieldsetInput
              label={t(
                'configuration-businessTrip.virtual-allowance-60eur-from',
              )}
              value={formatDate(
                new Date(state.tripPartEuropeModeStartFrom),
                'YYYY-MM-DD',
              )}
              name="tripPartEuropeModeStartFrom"
              placeholder=""
              onChange={(e) =>
                setValue(
                  'tripPartEuropeModeStartFrom',
                  formatDate(new Date(e.target.value), 'YYYY-MM-DDTHH:mm:ss'),
                )
              }
              disabled={
                readOnly || !('tripPartEuropeModeStartFrom' in state)
              }
              type="date"
            />

            {/* 
          PL_old: Tworzenie diet 60 EUR
          PL: “wirtualne diety” w wysokości 60 EUR wg
          API: tripPartEuropeMode
          */}
            <FieldsetSelect
              label={t(
                'configuration-businessTrip.virtual-allowance-60eur-mode',
              )}
              options={EUROPE_MODES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTripPartEuropeModeName(value, t),
              }))}
              value={state.tripPartEuropeMode.toString()}
              handleChange={(_, { option }) =>
                setValue('tripPartEuropeMode', option.key as TripPartEuropeMode)
              }
              name="tripPartEuropeMode"
              disabled={readOnly}
            />

            {/* 
          PL_old: Naliczaj zwrot kosztów noclegu
          PL: Naliczaj zwrot kosztów noclegu
          API: tripAccommodationInvoiceGeneration
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.accommodation-invoice')}
              checked={state.tripAccommodationInvoiceGeneration}
              onChange={handleChangeBoolean}
              name="tripAccommodationInvoiceGeneration"
              disabled={readOnly}
            />

            {/* 
          PL_old: Naliczaj sanitariaty
          PL: Naliczaj sanitariaty
          API: tripSanitarityMode
          */}
            <FieldsetSelect
              label={t('configuration-businessTrip.sanitary-mode')}
              options={SANITARITY_MODES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTripSanitarityModeName(value, t),
              }))}
              value={state.tripSanitarityMode.toString()}
              handleChange={(_, { option }) =>
                setValue('tripSanitarityMode', option.key as TripSanitarityMode)
              }
              name="tripSanitarityMode"
              disabled={readOnly}
            />

            {/* 
          PL: Nie naliczaj sanitariatów w dniach z ryczałtem noclegowym
          API: skipSanitarityOnAccommodationDays
          */}
            <FieldsetSwitch
              label={t(
                'configuration.businessTrips.skipSanitarityOnAccommodationDays',
              )}
              checked={state.skipSanitarityOnAccommodationDays}
              onChange={handleChangeBoolean}
              name="skipSanitarityOnAccommodationDays"
              disabled={readOnly}
            />
            {/* 
          PL_old: Koszty sanitariatów
          PL: Naliczaj koszt sanitariatów
          API: tripSanitarityCostMode
          */}
            <FieldsetRadio
              label={t('configuration-businessTrip.sanitary-cost-mode')}
              options={SANITARITY_COST_MODES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTripSanitarityCostModeName(value, t),
              }))}
              value={state.tripSanitarityCostMode.toString()}
              handleChange={(_, { option }) =>
                setValue(
                  'tripSanitarityCostMode',
                  option.key as TripSanitarityCostMode,
                )
              }
              name="tripSanitarityCostMode"
              disabled={readOnly}
            />
          </div>

          {/* 
        Konfiguracja państw
        */}
          <div
            data-tab-name="c"
            title={t('configuration-businessTrip.tab-countries')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <CompanyCountriesSection
              title={t('configuration-businessTrip.quick-select-countries')}
              companyId={companyId}
              readOnly={readOnly}
              max={PRIORITY_COUNTRIES_COUNT}
              errorMsg={
                hasInvalidPriorityCountries
                  ? t('configuration-businessTrip.quick-select-countries-required-count')
                  : undefined
              }
              selectedCountries={state.priorityCountries}
              setSelectedCountries={(priorityCountries) =>
                setState((state) => ({
                  ...state,
                  priorityCountries: toPriorityCountries(priorityCountries),
                }))
              }
            />
          </div>

          {/**
           * Automatyczne delegacje zbiorcze
           */}
          <div
            data-tab-name="e"
            title={t('configuration-businessTrip.tab-auto-collective')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-10"
          >
            {/* 
          PL_old: Importuj granice
          PL: Żródło inforamcji o przekoczeniu granicy
          API: generatedTripCountrySource
          */}
            <FieldsetSelect
              label={t('configuration-businessTrip.border-crossing-source')}
              options={GENERATED_TRIP_COUNTRY_SOURCE_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getGeneratedTripCountrySourceTypeName(value, t),
              }))}
              value={state.generatedTripCountrySource.toString()}
              // onChange={() => { }}
              handleChange={(_, { option }) =>
                setValue(
                  'generatedTripCountrySource',
                  option.key as GeneratedTripCountrySourceType,
                )
              }
              name="generatedTripCountrySource"
              disabled={readOnly}
            />

            {/* 
          PL_old: Rozszerzony przedział wyszukiwania granic
          PL: Rozszerzony zakres dat na wyszukiwania granic
          API: extendCountryChangesSearch
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.extended-border-search')}
              checked={state.extendCountryChangesSearch}
              onChange={handleChangeBoolean}
              name="extendCountryChangesSearch"
              disabled={readOnly}
            />

            {/* 
          PL_old: Uwzględniaj ładunki TMS
          PL: Wstawiaj automatycznie załadunki i rozładunki z TMS podczas tworzenia podróży służbowej
          API: includeTmsLoads
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.include-tms-loads')}
              checked={state.includeTmsLoads}
              onChange={handleChangeBoolean}
              name="includeTmsLoads"
              disabled={readOnly}
            />

            {/* 
          PL_old: Traktuj granice jako wyłączne z delegowania
          PL: Traktuj granice jako wyłączone z delegowania
          API: isGeneratedTripTransit
          */}
            <FieldsetSwitch
              label={t(
                'configuration-businessTrip.borders-excluded-from-delegation',
              )}
              checked={state.isGeneratedTripTransit}
              onChange={handleChangeBoolean}
              name="isGeneratedTripTransit"
              disabled={readOnly}
            />

            {/* 
          PL_old: Delegacje polskie rozpoczynaj od jazdy
          PL: Tworzenie podróży służbowych rozpoczynaj od zdarzenia jazdy
          API: startTripsFromDriving
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.start-trips-from-driving')}
              checked={state.startTripsFromDriving}
              onChange={handleChangeBoolean}
              name="startTripsFromDriving"
              disabled={readOnly}
            >
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-pl-1">
                <FieldLabel className="tw-mb-0 tw-max-w-xl tw-shrink tw-text-sm tw-font-medium">
                  {t(
                    'configuration-businessTrip.min-driving-minutes-start-on-driving',
                  )}
                </FieldLabel>
                <FieldsetInput
                  type="number"
                  min={0}
                  step={1}
                  className="tw-w-24"
                  value={String(state.minDrivingMinutesForStartOnDriving ?? 0)}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    setValue(
                      'minDrivingMinutesForStartOnDriving',
                      Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0,
                    )
                  }}
                  disabled={readOnly || !state.startTripsFromDriving}
                />
              </div>
            </FieldsetSwitch>

            {/* 
          PL_old: Delegacje polskie kończ na jeździe
          PL: Tworzenie delegacji krajowych kończ na zdarzeniu jazdy
          API: endTripsOnDriving
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.end-trips-on-driving')}
              checked={state.endTripsOnDriving}
              onChange={handleChangeBoolean}
              name="endTripsOnDriving"
              disabled={readOnly}
            >
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-pl-1">
                <FieldLabel className="tw-mb-0 tw-max-w-xl tw-shrink tw-text-sm tw-font-medium">
                  {t(
                    'configuration-businessTrip.min-driving-minutes-end-on-driving',
                  )}
                </FieldLabel>
                <FieldsetInput
                  type="number"
                  min={0}
                  step={1}
                  className="tw-w-24"
                  value={String(state.minDrivingMinutesForEndOnDriving ?? 0)}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    setValue(
                      'minDrivingMinutesForEndOnDriving',
                      Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0,
                    )
                  }}
                  disabled={readOnly || !state.endTripsOnDriving}
                />
              </div>
            </FieldsetSwitch>

            {/* 
          PL_old: Pomijaj wpisy manualne przy delegacjach do pakietu mobilności
          PL: Pomijaj wpisy manualne przy tworzeniu “wirtualnych delegacji” do pakietu mobilności
          API: skipManualEntrisOnAutoTrips
          API_optional: maxManualEntryOnAutoTrips
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.skip-manual-entries')}
              checked={state.skipManualEntrisOnAutoTrips}
              onChange={handleChangeBoolean}
              name="skipManualEntrisOnAutoTrips"
              disabled={readOnly}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <FieldLabel className="tw-mb-0 tw-whitespace-nowrap">
                  {t('configuration-businessTrip.longer-than')}
                </FieldLabel>
                <FieldsetInput
                  type="number"
                  value={state.maxManualEntryOnAutoTrips.toString()}
                  onChange={(e) =>
                    setValue(
                      'maxManualEntryOnAutoTrips',
                      Number(e.target.value),
                    )
                  }
                  step={1}
                  disabled={readOnly || !state.skipManualEntrisOnAutoTrips}
                />
              </div>
            </FieldsetSwitch>

            {/* 
          PL_old: Pomijaj braki danych przy delegacjach do pakietu mobilności
          PL: Pomijaj braki danych przy tworzeniu “wirtualnych delegacji” do pakietu mobilności
          API: skipNoDataOnAutoTrips
          API_optional: maxNoDataOnAutoTrips
          */}
            <FieldsetSwitch
              label={t('configuration-businessTrip.skip-no-data')}
              checked={state.skipNoDataOnAutoTrips}
              onChange={handleChangeBoolean}
              name="skipNoDataOnAutoTrips"
              disabled={readOnly}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <FieldLabel className="tw-mb-0 tw-whitespace-nowrap">
                  {t('configuration-businessTrip.longer-than')}
                </FieldLabel>
                <FieldsetInput
                  type="number"
                  value={state.maxNoDataOnAutoTrips.toString()}
                  onChange={(e) =>
                    setValue('maxNoDataOnAutoTrips', Number(e.target.value))
                  }
                  step={1}
                  disabled={readOnly || !state.skipNoDataOnAutoTrips}
                />
              </div>
            </FieldsetSwitch>
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>
  )
}

export const BusinessTripPage = withDefaultErrorBoundary(
  BusinessTripPageContent,
)
